import type { FieldStaffScheduleRow } from "./fieldCalendarUtils";
import { visitTimeRange } from "./fieldCalendarUtils";

/** Stable reserve-team user IDs (Roy, Leonard Tan). Do not match by name. */
export const RESERVE_TECHNICIAN_USER_IDS = [22, 23] as const;

export const SHOW_EMPTY_TECHNICIANS_LABEL = "Show technicians without schedule";
export const HIDE_EMPTY_TECHNICIANS_LABEL = "Hide technicians without schedule";

const RESERVE_SORT_ORDER = new Map<number, number>([
  [22, 0],
  [23, 1],
]);

export function isReserveTechnicianUserId(userId: number): boolean {
  return userId === 22 || userId === 23;
}

export function technicianRowHasOperationalActivity(
  row: Pick<FieldStaffScheduleRow, "visits" | "onLeave">,
): boolean {
  return (row.visits?.length ?? 0) > 0 || row.onLeave != null;
}

function earliestDisplayedVisitMins(row: FieldStaffScheduleRow): number {
  let earliest = Number.POSITIVE_INFINITY;
  for (const visit of row.visits ?? []) {
    const start = visitTimeRange(visit)?.startMins;
    if (start != null && Number.isFinite(start) && start < earliest) {
      earliest = start;
    }
  }
  return earliest;
}

export type TodayTechnicianPresentation = {
  visibleRows: FieldStaffScheduleRow[];
  emptyCount: number;
  showToggle: boolean;
};

/**
 * Presentation-only filter/order for Singapore Today technician rows.
 * Does not mutate rows or their visit/leave arrays.
 */
export function presentTodayTechnicianRows(
  rows: FieldStaffScheduleRow[],
  options: { applyTodayPresentation: boolean; showEmptyTechnicians: boolean },
): TodayTechnicianPresentation {
  const list = Array.isArray(rows) ? rows : [];
  if (!options.applyTodayPresentation) {
    return { visibleRows: list, emptyCount: 0, showToggle: false };
  }

  const indexed = list.map((row, originalIndex) => ({ row, originalIndex }));
  const emptyCount = indexed.filter((x) => !technicianRowHasOperationalActivity(x.row)).length;
  const candidates = options.showEmptyTechnicians
    ? indexed
    : indexed.filter((x) => technicianRowHasOperationalActivity(x.row));

  const nonReserve = candidates.filter((x) => !isReserveTechnicianUserId(x.row.userId));
  const reserve = candidates.filter((x) => isReserveTechnicianUserId(x.row.userId));

  nonReserve.sort((a, b) => {
    const aActive = technicianRowHasOperationalActivity(a.row);
    const bActive = technicianRowHasOperationalActivity(b.row);
    if (aActive !== bActive) return aActive ? -1 : 1;
    if (aActive && bActive) {
      const ta = earliestDisplayedVisitMins(a.row);
      const tb = earliestDisplayedVisitMins(b.row);
      const aHas = Number.isFinite(ta);
      const bHas = Number.isFinite(tb);
      if (aHas && bHas && ta !== tb) return ta - tb;
      if (aHas !== bHas) return aHas ? -1 : 1;
    }
    return a.originalIndex - b.originalIndex;
  });

  reserve.sort((a, b) => {
    const oa = RESERVE_SORT_ORDER.get(a.row.userId) ?? 99;
    const ob = RESERVE_SORT_ORDER.get(b.row.userId) ?? 99;
    if (oa !== ob) return oa - ob;
    return a.originalIndex - b.originalIndex;
  });

  return {
    visibleRows: [...nonReserve, ...reserve].map((x) => x.row),
    emptyCount,
    showToggle: emptyCount > 0,
  };
}
